import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useBcv } from '../context/BcvContext';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '584247329866';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CartPage() {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, clearCart, getCartTotal } = useCart();
  const { rate, toBolivares } = useBcv();
  const [customization, setCustomization] = useState({ nombre: '', numero: '' });

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    let message = '¡Hola Inversiones FU! Vengo de la App y quiero realizar este pedido:\n\n';

    cartItems.forEach((item) => {
      message += `- ${item.quantity}x ${item.descripcion} - Talla: ${item.selectedSize || 'N/A'} (${(item.precio * item.quantity).toFixed(2)} USD)\n`;
    });

    if (customization.nombre || customization.numero) {
      message += '\n🎯 Personalización:\n';
      if (customization.nombre) message += `Nombre: ${customization.nombre}\n`;
      if (customization.numero) message += `Número: ${customization.numero}\n`;
    }

    message += `\nTotal a pagar: $${getCartTotal().toFixed(2)} USD.\n`;
    message += '\n¿Me confirman disponibilidad y métodos de pago (Pago Móvil/Divisas)?';

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

    // Open WhatsApp
    window.open(whatsappUrl, '_blank');

    // Clear cart after sending
    toast.success('Redirigiendo a WhatsApp...', {
      description: 'Tu pedido será enviado al vendedor.',
    });
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white relative">
        <div className="relative z-10">
        <Header />
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <div className="text-8xl mb-4">🛒</div>
            <h1 className="text-4xl font-bold text-white">Tu carrito está vacío</h1>
            <p className="text-xl text-gray-300">
              ¡Añade algunas camisetas increíbles para empezar!
            </p>
            <Button
              onClick={() => navigate('/')}
              size="lg"
              className="bg-[#39FF14] hover:bg-[#39FF14]/90 text-black font-bold"
              style={{ boxShadow: '0 0 10px #39FF14' }}
            >
              Ver Catálogo
            </Button>
          </div>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative">
      <div className="relative z-10">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold mb-8">
            <ShoppingBag className="inline-block mr-3 h-10 w-10 text-primary" />
            Tu Carrito
          </h1>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => (
                <Card key={`${item.id}-${item.selectedSize}`} className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-24 h-24 rounded-lg bg-muted/30 flex-shrink-0 flex items-center justify-center overflow-hidden">
                        {item.images && item.images[0] ? (
                          <img src={item.images[0].startsWith('/') ? `${BACKEND_URL}${item.images[0]}` : item.images[0]} alt={item.descripcion} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-2xl font-bold text-muted-foreground/30">{item.descripcion.split(' ')[0].substring(0, 3)}</span>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg leading-tight mb-2">{item.descripcion}</h3>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-3">
                          <span className="px-2 py-1 rounded bg-primary/20 text-primary font-medium">
                            Talla: {item.selectedSize || 'N/A'}
                          </span>
                          <div className="flex flex-col">
                            <span className="font-bold text-foreground">${item.precio.toFixed(2)} USD</span>
                            {toBolivares(item.precio) && (
                              <span className="text-xs text-gray-400">Bs. {toBolivares(item.precio).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2 bg-muted/50 rounded-lg p-1">
                            <Button size="sm" variant="ghost" onClick={() => updateQuantity(item.id, item.selectedSize, item.quantity - 1)} className="h-8 w-8 p-0"><Minus className="h-4 w-4" /></Button>
                            <span className="w-8 text-center font-bold">{item.quantity}</span>
                            <Button size="sm" variant="ghost" onClick={() => updateQuantity(item.id, item.selectedSize, item.quantity + 1)} className="h-8 w-8 p-0"><Plus className="h-4 w-4" /></Button>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-lg font-bold text-neon">${(item.precio * item.quantity).toFixed(2)}</span>
                            {toBolivares(item.precio * item.quantity) && (
                              <span className="text-xs text-gray-400">Bs. {toBolivares(item.precio * item.quantity).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            )}
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => removeFromCart(item.id, item.selectedSize)} className="ml-auto text-destructive hover:text-destructive hover:bg-destructive/10">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Customization */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-xl">Personalización (Opcional)</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="nombre" className="text-sm font-medium mb-2 block">
                        Nombre a Estampar
                      </Label>
                      <Input
                        id="nombre"
                        placeholder="Ej: MESSI"
                        value={customization.nombre}
                        onChange={(e) => setCustomization({ ...customization, nombre: e.target.value })}
                        className="bg-background border-border"
                      />
                    </div>
                    <div>
                      <Label htmlFor="numero" className="text-sm font-medium mb-2 block">
                        Número a Estampar
                      </Label>
                      <Input
                        id="numero"
                        placeholder="Ej: 10"
                        value={customization.numero}
                        onChange={(e) => setCustomization({ ...customization, numero: e.target.value })}
                        className="bg-background border-border"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Total */}
                <Card className="bg-gradient-card border-primary/30">
                  <CardContent className="p-6 space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="font-medium">${getCartTotal().toFixed(2)}</span>
                      </div>
                      <Separator className="bg-border" />
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total USD</span>
                        <span className="text-neon text-2xl">${getCartTotal().toFixed(2)}</span>
                      </div>
                      {rate && (
                        <>
                          <Separator className="bg-border/50" />
                          <div className="flex justify-between text-sm">
                            <div>
                              <span className="text-gray-400 font-semibold">Total Bs.</span>
                              <span className="block text-[10px] text-gray-600">Tasa BCV: {rate.toLocaleString('es-VE', { minimumFractionDigits: 2 })}</span>
                            </div>
                            <span className="text-gray-300 font-black text-lg">
                              Bs. {toBolivares(getCartTotal()).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    <Button
                      onClick={handleCheckout}
                      size="lg"
                      className="w-full bg-[#39FF14] hover:bg-[#39FF14]/90 text-black font-bold text-base py-7 rounded-lg uppercase tracking-wide flex items-center justify-center gap-3"
                      style={{ boxShadow: '0 0 10px #39FF14' }}
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      FINALIZAR PEDIDO VÍA WHATSAPP
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                      Serás redirigido a WhatsApp para confirmar tu pedido y coordinar el pago
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      </div>
    </div>
  );
}
